import {
  BusinessLeasingType,
  FinancialLeasingType,
  TrimType,
  TypeOfLeasing,
} from '../../../../types';
import { SettingsStore } from '../store/settings-store';
import { FuelType } from '../types';
import {
  inclMomsToExMoms,
  percentToDecimal,
  pmt,
  yearlyToMonthlyCost,
} from './calc';
import {
  getBusinessForsakring,
  getBusinessRest,
  getBusinessService,
  getFinancialForsakring,
  getFinancialRest,
  getFinancialService,
} from './getCarValues';
import skatteverket from './skatteverket';

// Hämta från kpms
const defaultForbrukningBensinLitreperMile = 0.8;
const defaultForbrukningElkwhper100km = 15;

export const getElForbrukningPrice = (price?: number) => {
  if (price) {
    return price > 100 ? price / 10 : price;
  } else {
    return defaultForbrukningElkwhper100km;
  }
};

export const getBensinForbrukningPrice = (price?: number) => {
  return price ? price : defaultForbrukningBensinLitreperMile;
};

export default class CarCalculation {
  settings: SettingsStore;
  businessLeasing: BusinessLeasingType;
  financialLeasing: FinancialLeasingType;
  car: TrimType;

  constructor(trim: TrimType, settings: SettingsStore) {
    this.businessLeasing = trim.business_leasing
      ? trim.business_leasing[0]
      : undefined;
    this.financialLeasing = trim.financial_leasing
      ? trim.financial_leasing[0]
      : undefined;
    this.settings = settings;
    this.car = trim;
  }

  restValue() {
    if (this.settings.typeOfLeasing === TypeOfLeasing.BUSINESS) {
      return getBusinessRest(this.settings.mileage, this.businessLeasing);
    } else {
      return getFinancialRest(this.settings.mileage, this.financialLeasing);
    }
  }

  // include service and insurance?
  calculateFinancialLease() {
    if (!this.financialLeasing) return 0;
    return pmt({
      rate:
        percentToDecimal(
          this.businessLeasing.ranta ?? this.settings.interestRate,
        ) / 12,
      nper: 36,
      pv: -(
        this.financialLeasing.kalkylpris_exkl_moms -
        (this.settings.firstRent
          ? this.financialLeasing['1a_forhojd_hyra_20_ex_moms']
          : 0)
      ),
      fv: this.restValue(),
      type: 1,
    });
  }

  calculateBusinessLease() {
    if (!this.businessLeasing) return 0;
    return (
      pmt({
        rate:
          percentToDecimal(
            this.businessLeasing.ranta ?? this.settings.interestRate,
          ) / 12,
        nper: 36,
        pv: -(
          this.businessLeasing.ex_moms -
          (this.settings.firstRent
            ? this.businessLeasing.andra_inte_1a_hyra_ex_moms
            : 0)
        ),
        fv: this.restValue(),
        type: 1,
      }) +
      getBusinessService(this.settings.mileage, this.businessLeasing) +
      this.businessLeasing.vinterhjul_ex_moms +
      getBusinessForsakring(this.settings.mileage, this.businessLeasing)
    );
  }

  formansvardeGrundPris(price: number, type: FuelType = 'el') {
    switch (type.toLowerCase()) {
      case 'el':
        return price / 2 < price - skatteverket.schablonAvdragEl
          ? price - skatteverket.schablonAvdragEl
          : price / 2;
      case 'laddhybrid':
        return price / 2 < price - skatteverket.schablonAvdragLaddHybrid
          ? price - skatteverket.schablonAvdragLaddHybrid
          : price / 2;
      default:
        return price;
    }
  }

  calculateFormansvardeBransle() {
    const korstracka_privat = this.settings.mileage - this.settings.workMileage;
    let branslekostnad = this.fuelCost / 1.25;
    const formansvarde = this.settings.fuelBenefit
      ? branslekostnad * (korstracka_privat / this.settings.mileage) * 1.2
      : 0;

    return Math.round(formansvarde);
  }

  calculateFuelCost(mileage: number) {
    switch (this.car.type?.name.toLowerCase() as FuelType) {
      case 'el':
        return (
          (getElForbrukningPrice(this.car.electric_consumption) / 10) *
          mileage *
          this.settings.electricityPricePerKwh
        );
      case 'bensin':
      case 'hybrid':
        return (
          getBensinForbrukningPrice(this.car.fuel_consumption) *
          mileage *
          this.settings.gasPricePerLiter
        );
      case 'laddhybrid':
        const bensinkostnad =
          getBensinForbrukningPrice(this.car.fuel_consumption) *
          mileage *
          this.settings.gasPricePerLiter *
          (1 - this.settings.percentOnElectricity);
        const elkostnad =
          (getElForbrukningPrice(this.car.electric_consumption) / 10) *
          mileage *
          this.settings.electricityPricePerKwh *
          this.settings.percentOnElectricity;
        return Math.floor(bensinkostnad + elkostnad);
      default:
        return 0;
    }
  }

  get taxMonthly() {
    return yearlyToMonthlyCost(this.car.vehicle_tax);
  }

  // integrera mot api? https://www.skatteverket.se/privat/skatter/arbeteochinkomst/formaner/bilforman/bilformansberakning.4.3f4496fd14864cc5ac9539d.html
  get formansVarde() {
    const basbeloppsdel =
      skatteverket.prisbasBelopp * skatteverket.prisBasBeloppProcentsats;

    const formansGrundPris = this.formansvardeGrundPris(
      this.car.price,
      this.car.type ? (this.car.type.name as FuelType) : 'el',
    );

    const rantedelen = Math.floor(formansGrundPris * skatteverket.ranteProcent);

    const prisdelen = Math.floor(
      formansGrundPris * skatteverket.prisDelProcent,
    );

    const multiplier = this.settings.workMileage >= 3000 ? 0.75 : 1;

    const formansvarde =
      (basbeloppsdel + rantedelen + prisdelen + skatteverket.fordonsSkatt) *
      multiplier;

    return {
      formansvarde: formansvarde,
      formansvardeMonthly: yearlyToMonthlyCost(formansvarde),
    };
  }

  // caclulate monthly cost depending on type of leasing
  get monthlyCost() {
    return this.settings.typeOfLeasing === TypeOfLeasing.BUSINESS
      ? this.calculateBusinessLease()
      : this.calculateFinancialLease();
  }

  get fuelCost() {
    return this.calculateFuelCost(this.settings.mileage);
  }

  get fuelCostMonthly() {
    return yearlyToMonthlyCost(this.fuelCost);
  }

  get fuelCostMonthlyDriver() {
    return this.settings.fuelBenefit ? 0 : this.fuelCostMonthly;
  }

  get fuelCostMonthlyBusiness() {
    return this.settings.fuelBenefit
      ? inclMomsToExMoms(this.fuelCostMonthly)
      : 0;
  }

  get socialCostMonthly() {
    return Math.round(
      yearlyToMonthlyCost(
        this.formansVarde.formansvarde * skatteverket.arbetsgivaravgiftProcent,
      ),
    );
  }

  get socialCostFuelMonthly() {
    const price = this.settings.fuelBenefit
      ? yearlyToMonthlyCost(
          inclMomsToExMoms(
            this.calculateFuelCost(
              this.settings.mileage - this.settings.workMileage,
            ),
          ) * skatteverket.arbetsgivaravgiftProcent,
        )
      : 0;
    return Math.round(price);
  }

  get totalCostPersonal() {
    return (
      this.nettoFormansVarde.formansvarde_netto_monthly +
      this.fuelCostMonthlyDriver +
      this.nettoFormansVarde.formansvarde_bransle_netto_monthly
    );
  }

  get insuranceCost() {
    return this.settings.typeOfLeasing === TypeOfLeasing.BUSINESS
      ? 0
      : getFinancialForsakring(this.settings.mileage, this.financialLeasing);
  }

  get serviceCost() {
    return this.settings.typeOfLeasing === TypeOfLeasing.BUSINESS
      ? 0
      : getFinancialService(this.settings.mileage, this.financialLeasing);
  }

  get totalCostBusiness() {
    return (
      this.monthlyCost +
      this.taxMonthly +
      this.fuelCostMonthlyBusiness +
      this.socialCostMonthly +
      this.socialCostFuelMonthly +
      this.insuranceCost +
      this.serviceCost
    );
  }

  get nettoFormansVarde() {
    const formansvarde_netto =
      this.formansVarde.formansvarde * (1 - this.settings.percentTaxRate);
    const formansvarde_bransle_netto =
      this.calculateFormansvardeBransle() * (1 - this.settings.percentTaxRate);

    return {
      formansvarde_netto,
      formansvarde_bransle_netto,
      formansvarde_netto_monthly: formansvarde_netto / 12,
      formansvarde_bransle_netto_monthly: formansvarde_bransle_netto / 12,
    };
  }
}
